import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, formatCurrency } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../i18n';
import { 
  Wallet, Users, TrendingUp, Calendar, Plus, 
  ArrowRight, Bell, MessageCircle, ChevronRight,
  Banknote, Clock, PiggyBank
} from 'lucide-react';

export const DashboardPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [tontines, setTontines] = useState([]);
  const [catchupPlans, setCatchupPlans] = useState([]);
  const [acceptingPlanId, setAcceptingPlanId] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsResult, tontinesResult, catchupPlansResult] = await Promise.allSettled([
        api.getDashboardStats(),
        api.getTontines(),
        api.getMyCatchupPlans(),
      ]);

      if (statsResult.status !== 'fulfilled' || tontinesResult.status !== 'fulfilled') {
        throw new Error('Unable to load dashboard core data');
      }

      setStats(statsResult.value?.data || null);
      setTontines(tontinesResult.value?.data || []);
      setCatchupPlans(catchupPlansResult.status === 'fulfilled' ? (catchupPlansResult.value?.data?.plans || []) : []);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptCatchupPlan = async (planId) => {
    try {
      setAcceptingPlanId(planId);
      const response = await api.acceptCatchupPlan(planId);
      if (response?.data?.success) {
        toast.success(t('dashboardPage.catchup.acceptedToast'));
        await loadData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || t('dashboardPage.catchup.acceptError'));
    } finally {
      setAcceptingPlanId('');
    }
  };

  const getCatchupStatusLabel = (statusValue) => {
    if (!statusValue) {
      return '';
    }
    return t(`dashboardPage.catchup.status.${statusValue}`);
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboardPage.greetings.morning');
    if (hour < 18) return t('dashboardPage.greetings.afternoon');
    return t('dashboardPage.greetings.evening');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-orange-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-stone-900 dark:text-stone-50">
            {getGreeting()}, {user?.full_name?.split(' ')[0]}
          </h1>
          <p className="text-stone-600 dark:text-stone-400 mt-1">
            {t('dashboardPage.overviewSubtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/chat" data-testid="dashboard-chat-btn">
            <Button variant="outline" className="gap-2 w-full sm:w-auto">
              <MessageCircle className="w-4 h-4" />
              {t('dashboardPage.coachButton')}
            </Button>
          </Link>
          <Link to="/tontines/new" data-testid="dashboard-create-btn">
            <Button className="btn-accent gap-2 w-full sm:w-auto">
              <Plus className="w-4 h-4" />
              {t('dashboardPage.newTontine')}
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="stat-total-tontines">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500 dark:text-stone-400">{t('dashboardPage.stats.myTontines')}</p>
                <p className="text-3xl font-bold text-stone-900 dark:text-stone-50 mt-1 tabular-nums">
                  {stats?.total_tontines || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-stone-100 dark:bg-stone-800 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-stone-600 dark:text-stone-400" />
              </div>
            </div>
            <p className="text-xs text-green-600 mt-2">
              {t('dashboardPage.stats.activeCount', { count: stats?.active_tontines || 0 })}
            </p>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-contributed">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500 dark:text-stone-400">{t('dashboardPage.stats.totalContributed')}</p>
                <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 mt-1 tabular-nums">
                  {formatCurrency(stats?.total_contributed || 0)}
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                <Wallet className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-received">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500 dark:text-stone-400">{t('dashboardPage.stats.totalReceived')}</p>
                <p className="text-2xl font-bold text-stone-900 dark:text-stone-50 mt-1 tabular-nums">
                  {formatCurrency(stats?.total_received || 0)}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-pending">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-stone-500 dark:text-stone-400">{t('dashboardPage.stats.pending')}</p>
                <p className="text-3xl font-bold text-stone-900 dark:text-stone-50 mt-1 tabular-nums">
                  {stats?.pending_contributions || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-2">{t('dashboardPage.stats.contributionsSuffix')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions + Tontines List */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* My Tontines */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold text-stone-900 dark:text-stone-50">
              {t('dashboardPage.myTontinesTitle')}
            </h2>
            <Link to="/tontines" className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1">
              {t('dashboardPage.viewAll')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {tontines.length === 0 ? (
            <Card className="card-neo">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-stone-100 dark:bg-stone-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <PiggyBank className="w-8 h-8 text-stone-400" />
                </div>
                <h3 className="font-display text-lg font-semibold text-stone-900 dark:text-stone-50 mb-2">
                  {t('dashboardPage.empty.title')}
                </h3>
                <p className="text-stone-500 dark:text-stone-400 mb-6 max-w-sm mx-auto">
                  {t('dashboardPage.empty.description')}
                </p>
                <Link to="/tontines/new">
                  <Button className="btn-accent">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('dashboardPage.empty.createButton')}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {tontines.slice(0, 3).map((tontine) => (
                <Link 
                  key={tontine.id} 
                  to={`/tontines/${tontine.id}`}
                  data-testid={`tontine-card-${tontine.id}`}
                >
                  <Card className="card-neo hover:-translate-y-0.5 transition-transform cursor-pointer">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-stone-900 dark:text-stone-50 line-clamp-2 break-words pr-2">
                              {tontine.name}
                            </h3>
                            {tontine.admin_id === user?.id && (
                              <Badge
                                variant="outline"
                                className="text-xs border-orange-200 text-orange-700"
                                data-testid={`dashboard-admin-badge-${tontine.id}`}
                                title="Vous êtes admin de cette tontine"
                              >
                                {t('tontine.admin')}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-stone-500 dark:text-stone-400">
                            {t('dashboardPage.membersCount', { current: tontine.current_members, max: tontine.max_members })}
                          </p>
                        </div>
                        <Badge 
                          variant={tontine.status === 'active' ? 'default' : 'secondary'}
                          className={tontine.status === 'active' ? 'bg-green-100 text-green-700 hover:bg-green-100' : ''}
                        >
                          {tontine.status === 'active' ? t('dashboardPage.status.active') : tontine.status}
                        </Badge>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
                          <span className="text-stone-600 dark:text-stone-400">
                            {t('dashboardPage.contributionMonthly', { amount: formatCurrency(tontine.contribution_amount, tontine.currency) })}
                          </span>
                          <span className="text-stone-400">•</span>
                          <span className="text-stone-600 dark:text-stone-400">
                            {t('dashboardPage.roundLabel', { round: tontine.current_round })}
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-stone-400" />
                      </div>
                      <Progress 
                        value={(tontine.current_members / tontine.max_members) * 100} 
                        className="h-1.5 mt-3"
                      />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <h2 className="font-display text-xl font-bold text-stone-900 dark:text-stone-50">
            {t('dashboardPage.quickActionsTitle')}
          </h2>
          
          <Card className="card-neo">
            <CardContent className="p-0">
              <Link 
                to="/tontines/new" 
                className="flex items-center gap-4 p-4 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors border-b border-stone-100 dark:border-stone-800"
                data-testid="quick-action-create"
              >
                <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                  <Plus className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-900 dark:text-stone-50">{t('dashboardPage.quick.createTitle')}</p>
                  <p className="text-sm text-stone-500">{t('dashboardPage.quick.createDesc')}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400" />
              </Link>

              <Link 
                to="/contributions" 
                className="flex items-center gap-4 p-4 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors border-b border-stone-100 dark:border-stone-800"
                data-testid="quick-action-contribute"
              >
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                  <Banknote className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-900 dark:text-stone-50">{t('dashboardPage.quick.contributeTitle')}</p>
                  <p className="text-sm text-stone-500">{t('dashboardPage.quick.contributeDesc')}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400" />
              </Link>

              <Link 
                to="/chat" 
                className="flex items-center gap-4 p-4 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
                data-testid="quick-action-chat"
              >
                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-stone-900 dark:text-stone-50">{t('dashboardPage.quick.chatTitle')}</p>
                  <p className="text-sm text-stone-500">{t('dashboardPage.quick.chatDesc')}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-stone-400" />
              </Link>
            </CardContent>
          </Card>

          {/* Next payment reminder */}
          {stats?.next_contribution_amount && (
            <Card className="card-neo bg-gradient-to-br from-orange-50 to-orange-100/50 dark:from-orange-900/20 dark:to-orange-900/10 border-orange-200 dark:border-orange-800" data-testid="next-contribution-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Bell className="w-5 h-5 text-orange-600" />
                  <p className="font-medium text-orange-900 dark:text-orange-100">{t('dashboardPage.nextContributionTitle')}</p>
                </div>
                <p className="text-2xl font-bold text-orange-700 dark:text-orange-400 tabular-nums" data-testid="next-contribution-amount">
                  {formatCurrency(stats.next_contribution_amount)}
                </p>
                <p className="text-sm text-orange-600 dark:text-orange-500 mt-1">
                  {t('dashboardPage.nextContributionDue')}
                </p>
              </CardContent>
            </Card>
          )}

          {catchupPlans.length > 0 && (
            <Card className="card-neo" data-testid="catchup-plans-card">
              <CardContent className="p-5 space-y-3">
                <p className="font-medium text-stone-900 dark:text-stone-50">{t('dashboardPage.catchup.title')}</p>
                {catchupPlans.slice(0, 4).map((plan) => (
                  <div key={plan.id} className="rounded-lg border border-stone-200 p-3 space-y-2" data-testid={`catchup-plan-item-${plan.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-stone-900 dark:text-stone-50">{plan.tontine_name || t('dashboardPage.myTontinesTitle')}</p>
                      <Badge variant="outline" data-testid={`catchup-plan-status-${plan.id}`}>
                        {getCatchupStatusLabel(plan.status)}
                      </Badge>
                    </div>
                    <p className="text-xs text-stone-500">
                      {t('dashboardPage.catchup.installments', { count: plan.installments || 0 })} • {formatCurrency(plan.amount_total || 0, plan.currency || 'XOF')}
                    </p>
                    {plan.status === 'proposed' && (
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() => handleAcceptCatchupPlan(plan.id)}
                        disabled={acceptingPlanId === plan.id}
                        data-testid={`catchup-plan-accept-${plan.id}`}
                      >
                        {acceptingPlanId === plan.id ? t('common.loading') : t('dashboardPage.catchup.acceptButton')}
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
